/**
 * TaskDetailPanel — Enhanced task detail panel for Workflow State View.
 *
 * Thin wrapper around EntityDetailPanel that:
 * - Bridges TaskFieldsFragment → PrimitiveItem
 * - Injects custom Details content with field sections (TaskDetailsContent)
 * - Wires task-specific tabs: Dependencies (Cytoscape mini-DAG),
 *   Workflow Map (full DAG minimap), Schedule (FullCalendar)
 * - Gets Audit, Timeline, Context, Documents, Notes, Compare tabs for free
 *
 * DRR-111: Enhanced Task Detail Panel
 * DUMB FRONTEND: All data from backend via GraphQL.
 */

import { useCallback } from 'react';
import type { TaskFieldsFragment } from '@/generated/graphql';
import type { PrimitiveItem } from '@/hooks/usePrimitiveData';
import { EntityDetailPanel } from '@/components/entity-detail';
import { TaskDetailsContent } from './TaskDetailsContent';
import { useTaskSiblings } from './useTaskSiblings';
import { TaskDependencyTab } from './tabs/TaskDependencyTab';
import { TaskWorkflowMapTab } from './tabs/TaskWorkflowMapTab';

// ============================================================================
// PROPS
// ============================================================================

interface TaskDetailPanelProps {
  /** The selected task (or null if nothing selected) */
  selectedTask: TaskFieldsFragment | null;
  /** Whether the panel is open */
  opened: boolean;
  /** Close callback */
  onClose: () => void;
  /** All tasks loaded by the board (for sibling context) */
  allTasks: TaskFieldsFragment[];
  /** Refetch board data after mutations */
  onRefetch: () => void;
  /** Whether in demo mode (mutations disabled) */
  demoMode?: boolean;
  /** Navigate to a different task (for graph node clicks) */
  onTaskSelect?: (task: TaskFieldsFragment) => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function TaskDetailPanel({
  selectedTask,
  opened,
  onClose,
  allTasks,
  onRefetch,
  demoMode = false,
  onTaskSelect,
}: TaskDetailPanelProps) {
  const siblingInfo = useTaskSiblings(allTasks, selectedTask);

  // Bridge TaskFieldsFragment → PrimitiveItem
  const entity: PrimitiveItem | null = selectedTask
    ? { ...selectedTask } as PrimitiveItem
    : null;

  // Default task select: just update the panel to the clicked task
  const handleTaskSelect = useCallback(
    (task: TaskFieldsFragment) => {
      if (onTaskSelect) {
        onTaskSelect(task);
      }
    },
    [onTaskSelect],
  );

  // Custom Details tab content with sections + SLA countdown
  const detailsContent = selectedTask ? (
    <TaskDetailsContent task={selectedTask} siblingInfo={siblingInfo} />
  ) : null;

  // Dependencies tab: Cytoscape mini-DAG
  const dependenciesContent = (
    <TaskDependencyTab
      currentTask={selectedTask}
      allTasks={allTasks}
      onTaskSelect={handleTaskSelect}
    />
  );

  // Workflow Map tab: full DAG minimap of all siblings
  const workflowMapContent = (
    <TaskWorkflowMapTab
      currentTask={selectedTask}
      siblingTasks={siblingInfo.siblings}
      onTaskSelect={handleTaskSelect}
    />
  );

  return (
    <EntityDetailPanel
      entity={entity}
      primitiveType="task"
      opened={opened}
      onClose={onClose}
      isReadOnly={demoMode}
      onEntityUpdated={onRefetch}
      detailsContent={detailsContent}
      tabContentOverrides={{
        dependencies: dependenciesContent,
        'workflow-map': workflowMapContent,
      }}
      statusColorMap={{
        PENDING: 'yellow',
        READY: 'cyan',
        ASSIGNED: 'blue',
        IN_PROGRESS: 'blue',
        BLOCKED: 'orange',
        PAUSED: 'gray',
        PENDING_EXTERNAL: 'grape',
        COMPLETED: 'green',
        FAILED: 'red',
      }}
      allEntities={allTasks.map(t => ({ ...t }) as PrimitiveItem)}
    />
  );
}
